<script lang="ts">
	/**
	 * The single block a list, grid or table body renders when it has nothing to
	 * show — because it is loading, because it is genuinely empty, or because it
	 * failed.
	 *
	 * All three are the same shape (centred, generously padded, muted) so the
	 * area does not jump as a list moves between them; only the icon and the
	 * colour change. Having one component for all three is what stops each page
	 * from inventing its own spinner-in-a-div.
	 *
	 * Inside a Flowbite table, wrap it:
	 * `<TableBodyRow class="hover:bg-gray-50 dark:hover:bg-gray-600"><TableBodyCell colspan={n}><EmptyState … /></TableBodyCell></TableBodyRow>`.
	 */

	import { Spinner } from 'flowbite-svelte';
	import type { Snippet } from 'svelte';
	import { t } from '../i18n';

	interface Props {
		/** Which of the three states this is. */
		variant?: 'empty' | 'loading' | 'error';
		/** Headline. Falls back to a sensible default per variant. */
		title?: string;
		/** Legacy alias for `title`. */
		heading?: string;
		/** Optional second line explaining what to do about it. */
		description?: string;
		/** Font Awesome solid icon name, without the `fa-` prefix. Ignored while loading. */
		faIcon?: string;
		/** Legacy alias for `faIcon`, accepting the full `fa-…` class. */
		icon?: string;
		/** Tighter padding and no icon — for narrow columns and small panels. */
		compact?: boolean;
		/** Extra classes for the wrapper. */
		class?: string;
		/** Actions ("Create the first one", "Retry"). */
		children?: Snippet;
	}

	let {
		variant = 'empty',
		title = '',
		heading = '',
		description = '',
		faIcon = '',
		icon = '',
		compact = false,
		class: className = '',
		children
	}: Props = $props();

	/** Headline used when the caller supplies none. */
	let defaultTitle = $derived(
		{
			empty: $t('Nothing here yet'),
			loading: $t('Loading…'),
			error: $t('Something went wrong')
		}[variant]
	);

	/** Icon used when the caller supplies none. Loading shows a spinner instead. */
	const defaultIcons: Record<string, string> = {
		empty: 'inbox',
		loading: '',
		error: 'triangle-exclamation'
	};

	let resolvedTitle = $derived(title || heading || defaultTitle);
	/* `icon` is the older prop and carries the `fa-` prefix; strip it so both spellings work. */
	let resolvedIcon = $derived(faIcon || icon.replace(/^fa-/, '') || defaultIcons[variant]);
	/* Errors are the one case worth colouring — everything else stays muted so an
	   empty list never looks like a warning. */
	let toneClass = $derived(variant === 'error' ? 'text-red-500 dark:text-red-400' : 'text-default');
</script>

<div
	class="{className} flex w-full flex-col items-center justify-center {compact
		? 'gap-1 p-2'
		: 'gap-3 p-10'}"
>
	{#if variant === 'loading'}
		<Spinner size={compact ? '5' : '8'} />
	{:else if resolvedIcon && !compact}
		<i class="fa-solid fa-{resolvedIcon} fa-2x {toneClass} opacity-60"></i>
	{/if}

	<div class="text-center {toneClass} {compact ? 'text-xs font-medium uppercase' : ''}">
		{resolvedTitle}
	</div>

	{#if description && !compact}
		<div class="text-default max-w-md text-center text-sm">{description}</div>
	{/if}

	{#if children}
		<div class="flex flex-wrap items-center justify-center gap-2 {compact ? '' : 'mt-2'}">
			{@render children()}
		</div>
	{/if}
</div>
